import { autoUpdate, flip, offset, useFloating, useHover } from '@floating-ui/react'
import cn from 'classnames'
import copy from 'copy-to-clipboard'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaClipboard, FaClipboardCheck } from 'react-icons/fa'
import styled, { css } from 'styled-components'
import { Shimmer } from '../Shimmer/Shimmer'

interface Props {
  value?: string | null
  maxLines?: 1 | 2
  isLoading?: boolean
}

const CLOSE_DELAY = 1000
const OPEN_DELAY = 1400

export const CopyValue: React.FC<Props> = ({ value, maxLines = 1, isLoading = false }) => {
  const [t] = useTranslation()
  const [infoOpen, setInfoOpen] = useState(false)
  const [valueCopied, setValueCopied] = useState(false)

  const { context, x, y, refs, strategy } = useFloating({
    open: infoOpen,
    onOpenChange: setInfoOpen,
    whileElementsMounted: autoUpdate,
    placement: 'bottom-end',
    middleware: [offset(5), flip()]
  })

  useHover(context, {
    enabled: !isLoading,
    delay: {
      open: OPEN_DELAY,
      close: CLOSE_DELAY
    }
  })

  const copyToClipboard = (valueToCopy: string) => {
    if (isLoading || valueCopied || !valueToCopy) return
    copy(valueToCopy)
    setInfoOpen(true)
    setValueCopied(true)
  }

  const onMouseLeave = () => {
    if (!valueCopied) return
    setTimeout(() => setValueCopied(false), CLOSE_DELAY)
  }

  return (
    <>
      <StyledCopyValueWrapper
        ref={refs.setReference}
        $maxLines={maxLines}
        $isLoading={isLoading}
        onClick={() => copyToClipboard(value || '')}
        onMouseLeave={() => onMouseLeave()}
      >
        <StyledCopyValue $maxLines={maxLines}>
          {isLoading ? (
            <ShimmerSlot>
              <Shimmer width="100%" height="0.875rem" borderRadius="0.35rem" />
            </ShimmerSlot>
          ) : (
            value
          )}
        </StyledCopyValue>

        <StyledCopyButton
          type="button"
          disabled={isLoading}
          className={cn({ success: valueCopied })}
        >
          {valueCopied ? <FaClipboardCheck /> : <FaClipboard />}
        </StyledCopyButton>
      </StyledCopyValueWrapper>

      <AnimatePresence>
        {infoOpen && !isLoading && (
          <StyledTooltip
            ref={refs.setFloating}
            style={{
              top: y || 0,
              left: x || 0,
              position: strategy,
              width: 'max-content'
            }}
          >
            {valueCopied ? t('components.copyValue.copied') : t('components.copyValue.copy')}
          </StyledTooltip>
        )}
      </AnimatePresence>
    </>
  )
}

const StyledTooltip = styled(motion.div).attrs({
  initial: { opacity: 0, y: -10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 }
})`
  background: ${({ theme }) => theme.colors.menuBg};
  color: ${({ theme }) => theme.colors.neutralWhite};
  padding: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  border-radius: 0.6rem;
`

const StyledCopyValueWrapper = styled.div<{ $maxLines: 1 | 2; $isLoading: boolean }>`
  display: flex;
  align-items: ${({ $maxLines }) => ($maxLines > 1 ? 'flex-start' : 'stretch')};
  width: 100%;
  max-width: 100%;
  background: ${({ theme }) => theme.colors.defaultHover};
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  overflow: hidden;
  cursor: ${({ $isLoading }) => ($isLoading ? 'default' : 'pointer')};
  color: ${({ theme }) => theme.colors.menuBg};
  min-width: 0;
  pointer-events: ${({ $isLoading }) => ($isLoading ? 'none' : 'auto')};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const StyledCopyValue = styled.div<{ $maxLines: 1 | 2 }>`
  flex: 1;
  padding: 8px 10px;
  font-family: 'Roboto Mono', monospace;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  min-width: 0;

  ${({ $maxLines }) =>
    $maxLines === 1
      ? css`
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        `
      : css`
          white-space: normal;
          overflow-wrap: anywhere;
          word-break: break-word;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        `}
`

const ShimmerSlot = styled.span`
  display: block;
  line-height: 0;
`

const StyledCopyButton = styled.button`
  outline: none !important;
  border: none;
  flex-shrink: 0;
  padding: 8px 10px;
  background: none;
  color: ${({ theme }) => theme.colors.iconGray};

  svg {
    transition: 0.4s;
  }

  &.success {
    svg {
      fill: ${({ theme }) => theme.colors.primary} !important;
    }
  }
`
